"use client";

import React, { useEffect, useState } from "react";
import { Icon } from "./icon";

interface Project {
  id: string;
  name: string;
}

interface OnboardingNavbarProps {
  selectedProject: Project;
  projectDropdownOptions: Project[];
  onProjectSelect: (project: Project) => void;
  address?: string;
  weatherInfo?: string;
  notifications?: number;
}

export const OnboardingNavbar = ({
  selectedProject: initialSelectedProject,
  projectDropdownOptions,
  onProjectSelect,
  address = "🌱 217 Redwood Crest Lane",
  weatherInfo = "☀️65 °F, Austin, TX",
  notifications = 0,
}: OnboardingNavbarProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(
    initialSelectedProject,
  );

  useEffect(() => {
    setSelectedProject(initialSelectedProject);
  }, [initialSelectedProject]);

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    onProjectSelect(project);
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex items-center justify-between rounded-lg bg-white/80 px-2 py-2 backdrop-blur-[40px] sm:px-4 sm:py-3">
      {/* Left section - Logo and Project Selector (on larger screens) */}
      <div className="flex items-center gap-2 sm:gap-7 lg:flex-1">
        <div className="relative h-[18px] w-[75px] sm:h-[22px] sm:w-[90px]">
          <Icon name="yardzen-logo-full" className="h-full w-full" />
        </div>

        {/* Project Selector - Only visible on lg screens */}
        <div className="relative hidden lg:block">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-7 rounded-full border border-black/5 px-4 py-2 hover:bg-gray-50"
          >
            <span className="text-[18px] font-normal leading-[1.2] tracking-[0.01em]">
              {selectedProject.name}
            </span>
            <span className="text-base text-gray-800">▼</span>
          </button>

          {isDropdownOpen && (
            <div className="absolute left-0 top-full z-10 mt-1 w-full rounded-lg border border-gray-100 bg-white py-1 shadow-lg">
              {projectDropdownOptions.map((project) => (
                <button
                  key={project.id}
                  className={`w-full px-4 py-2 text-left text-[16px] hover:bg-gray-50 ${
                    project.id === selectedProject.id
                      ? "bg-gray-50 font-medium"
                      : ""
                  }`}
                  onClick={() => handleProjectSelect(project)}
                >
                  {project.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Center Project Selector - Only visible on smaller screens */}
      <div className="relative lg:hidden">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 rounded-full border border-black/5 px-2 py-1 hover:bg-gray-50 sm:gap-4 sm:px-3 sm:py-1.5"
        >
          <span className="max-w-[120px] truncate text-sm font-normal leading-[1.2] tracking-[0.01em] sm:max-w-[160px] sm:text-base">
            {selectedProject.name}
          </span>
          <span className="text-sm text-gray-800 sm:text-base">▼</span>
        </button>

        {isDropdownOpen && (
          <div className="absolute left-1/2 top-full z-10 mt-1 w-[200px] -translate-x-1/2 rounded-lg border border-gray-100 bg-white py-1 shadow-lg sm:w-[240px]">
            {projectDropdownOptions.map((project) => (
              <button
                key={project.id}
                className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 sm:px-4 sm:py-2 sm:text-base ${
                  project.id === selectedProject.id
                    ? "bg-gray-50 font-medium"
                    : ""
                }`}
                onClick={() => handleProjectSelect(project)}
              >
                {project.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Center section - Address and Weather */}
      <div className="hidden flex-1 items-center justify-center gap-4 lg:flex">
        <button className="flex items-center justify-center rounded-full border border-black/5 px-2 py-1 text-xs font-medium leading-6">
          {address}
        </button>
        <button className="flex items-center justify-center rounded-full border border-black/5 px-2 py-1 text-xs font-medium leading-6">
          {weatherInfo}
        </button>
      </div>

      {/* Right section - Notifications */}
      <div className="flex items-center justify-end lg:flex-1">
        {notifications > 0 ? (
          <div
            className="relative cursor-pointer"
            onClick={() => {
              console.log("clicked");
            }}
          >
            <Icon
              name="chat3"
              className="h-[18px] w-[16px] sm:h-[20px] sm:w-[18px]"
            />
            <span className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-[#E0ED94] text-[8px] font-semibold text-[#7C8971] sm:h-4 sm:w-4 sm:text-[10px]">
              {notifications}
            </span>
          </div>
        ) : (
          <Icon
            name="chat3"
            className="h-[18px] w-[16px] cursor-pointer sm:h-[20px] sm:w-[18px]"
            onClick={() => {
              console.log("clicked");
            }}
          />
        )}
      </div>
    </div>
  );
};

export default OnboardingNavbar;
